import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { IFeatures } from '@bciti/types';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';

import { State } from '../../store';
import { organisationFeaturesConfigurationSelector, organisationTagSelector } from '../../global/store/global.selectors';
import { ExternalSystemsService } from '../../global/services/external-systems/external-systems.abstract.service';
import { Connector } from '../../global/services/external-systems/external-systems.model';

/** The connector type the permit module is driven by. */
const PERMIT_CONNECTOR_TYPE = 'Permits';

/**
 * `permits.sync` is not in `IFeatures` yet. Widen the type here rather than blocking on a
 * @bciti/types release; drop the intersection once the package carries the field.
 */
type PermitFeatures = IFeatures & { permits?: { sync?: boolean } };

@Injectable()
export class PermitSettingsService {
    /**
     * True when this organisation's permits are driven by an external system (e.g. PSD CityWide),
     * which then owns the workflow — and therefore the statuses.
     *
     * Sourced from the org features config already in the store, the same way the request module
     * reads `requests.sync` / `requestCategories.sync`, so there is no request to make and an
     * organisation switch re-emits on its own. Absent config reads as false: an org that has not
     * been flagged keeps full control of its statuses.
     */
    readonly isExternallyManaged$: Observable<boolean>;

    /**
     * True when this organisation actually has a Permits-type connector configured.
     *
     * Distinct from `isExternallyManaged$`, which reads the `permits.sync` features
     * flag: that flag says the workflow is externally owned, not that a connector
     * exists. Anything offering to talk to the connector — a manual sync — must
     * gate on this instead, or it would offer an action the backend rejects.
     *
     * A failure to read the configuration reads as "no connector": hiding an
     * action is the safe direction when we cannot tell.
     */
    readonly hasPermitConnector$: Observable<boolean>;

    constructor(
        private readonly store: Store<State>,
        private readonly externalSystemsService: ExternalSystemsService,
    ) {
        this.isExternallyManaged$ = this.store.select(organisationFeaturesConfigurationSelector).pipe(
            map((features: PermitFeatures) => features?.permits?.sync === true),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: false }),
        );

        this.hasPermitConnector$ = this.store.select(organisationTagSelector).pipe(
            distinctUntilChanged(),
            switchMap((orgTag) =>
                orgTag
                    ? this.externalSystemsService.getConfiguredConnectors(orgTag).pipe(
                          map((connectors: Connector[]) => (connectors || []).some((connector) => connector.type === PERMIT_CONNECTOR_TYPE)),
                          catchError(() => of(false)),
                      )
                    : of(false),
            ),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
    }
}
